import { Button, Card } from "@ui/shad"
import { cn } from "@ui/utils"
import { Check, ShieldCheck } from "lucide-react"

import { useActionLink, useExecuteActionLink } from "@/hooks/api/action-links"

interface DataActionApprovalProps {
  id: string
  disabled?: boolean
}

export default function DataActionApproval({ id, disabled = false }: DataActionApprovalProps) {
  const { data: actionLink } = useActionLink(id)
  const { mutate: execute, isPending } = useExecuteActionLink()

  if (!actionLink) return null

  const isExecuted = !!actionLink.executed_at
  const isExpired = new Date(actionLink.expires_at) < new Date()
  const isDisabled = disabled || isPending || isExecuted || isExpired
  const summary = typeof actionLink.properties?.summary === "string" ? actionLink.properties.summary : "Apply changes"
  const planCount = typeof actionLink.properties?.plan_count === "number" ? actionLink.properties.plan_count : 1
  const details = Array.isArray(actionLink.properties?.details)
    ? actionLink.properties.details.filter((detail: unknown): detail is string => typeof detail === "string")
    : []
  const actions = Array.isArray(actionLink.properties?.actions) ? actionLink.properties.actions : []
  const validatedAction =
    actions.length === 1 && typeof actions[0] === "object" && actions[0] !== null ? actions[0] : undefined
  const validatedSummary =
    validatedAction && "summary" in validatedAction && typeof validatedAction.summary === "string"
      ? validatedAction.summary
      : undefined
  const validatedDetails =
    validatedAction && "details" in validatedAction && Array.isArray(validatedAction.details)
      ? validatedAction.details.filter((detail: unknown): detail is string => typeof detail === "string")
      : undefined
  const displaySummary = validatedSummary || summary
  const displayDetails: string[] = validatedDetails || details

  return (
    <Card className="w-full max-w-xl space-y-3 p-4">
      <div className="flex items-start gap-3">
        <ShieldCheck className="text-blue-foreground mt-0.5 size-5 shrink-0" />
        <div className="min-w-0">
          <p className="font-medium">{displaySummary}</p>
          <p className="text-muted-foreground text-sm">
            {planCount > 1
              ? `Review these ${planCount} changes before applying them.`
              : "Review this change before applying it."}
          </p>
        </div>
      </div>
      {displayDetails.length > 0 && (
        <ul className="bg-tinted space-y-1 rounded-md p-3 text-sm">
          {displayDetails.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}
      {actions.length > 1 && (
        <details className="bg-tinted rounded-md p-3 text-sm">
          <summary className="cursor-pointer font-medium">Review all {actions.length} actions</summary>
          <ul className="mt-2 space-y-2">
            {actions.map((action, index) => (
              <li key={`${action.summary || "Action"}-${index}`}>
                <p>{action.summary || `Action ${index + 1}`}</p>
                {action.details?.map((detail: string) => (
                  <p key={detail} className="text-muted-foreground">
                    {detail}
                  </p>
                ))}
              </li>
            ))}
          </ul>
        </details>
      )}
      <Button
        variant={isExecuted || isExpired ? "secondary" : "primary"}
        disabled={isDisabled}
        onClick={() => execute({ id })}
        className={cn(
          isExecuted && "border-green-border bg-green-background text-green-foreground hover:bg-green-border",
          isExpired && "cursor-default"
        )}
      >
        {isExecuted && <Check />}
        {isPending
          ? "Approving..."
          : isExecuted
            ? "Approved"
            : isExpired
              ? "Approval expired"
              : disabled
                ? "Finishing response..."
                : "Approve and apply"}
      </Button>
    </Card>
  )
}
